<script lang="ts" setup>
import { ButtonQC, IconQC } from '@qicgroupdoha/qic-connect-ds';
import { useMutation } from '@tanstack/vue-query';
import { ref } from 'vue';
import { Rating, RatingMap } from '@/features/FeedbackForm/lib/FeedbackForm.constants';
import { GA_EVENTS, useGoogleAnalytics } from '@/shared/analytics';
import { BaseModal } from '@/shared/ui/BaseModal';
import { FlowButton } from '@/shared/ui/FlowButton';
import { useFeedbackService } from '../api/Feedback.service';
import emojiNeutral from '../assets/images/emoji_neutral.svg';
import emojiSad from '../assets/images/emoji_sad.svg';
import emojiSmile from '../assets/images/emoji_smile.svg';
import emojiSorry from '../assets/images/emoji_sorry.svg';

const show = defineModel<boolean>();

const gaService = useGoogleAnalytics();
const isShowFeedbackCommentPopup = ref(false);
const comment = ref('');
const rating = ref<Rating>(Rating.GOOD);

function onCloseFeedback() {
  show.value = false;
}

function onClosePopup() {
  isShowFeedbackCommentPopup.value = false;
  show.value = false;
}

const sendFeedbackApi = useFeedbackService();
const {
  mutateAsync: sendFeedback,
  isSuccess: isFormSent,
  isPending: isSendFeedbackPending,
} = useMutation({
  mutationFn: sendFeedbackApi.sendFeedback,
});

function onRate(userRating: Rating) {
  rating.value = userRating;

  if (rating.value === Rating.BAD) {
    isShowFeedbackCommentPopup.value = true;
  } else {
    sendFeedback({
      rating: RatingMap[rating.value],
    });

    onCloseFeedback();
  }

  gaService.send(GA_EVENTS.RATE_US, {
    value: rating.value,
  });
}

async function onSendFeedback() {
  await sendFeedback({
    rating: RatingMap[rating.value],
    comment: comment.value,
  });
}
</script>

<template>
  <div class="feedback">
    <button class="feedback__close" data-test="feedback-close" @click="onCloseFeedback">
      <IconQC name="x-close" />
    </button>

    <div class="feedback__title">
      {{ $t('qic.rate.title') }}
    </div>
    <div class="feedback__container">
      <button class="feedback__item feedback__item--good" data-test="feedback-good" @click="onRate(Rating.GOOD)">
        <img :src="emojiSmile" alt="emojiSmile">
      </button>
      <button class="feedback__item feedback__item--normal" data-test="feedback-normal" @click="onRate(Rating.NORMAL)">
        <img :src="emojiNeutral" alt="emojiNeutral">
      </button>
      <button class="feedback__item feedback__item--sad" data-test="feedback-sad" @click="onRate(Rating.BAD)">
        <img :src="emojiSad" alt="emojiSad">
      </button>
    </div>
  </div>

  <BaseModal :is-open="isShowFeedbackCommentPopup" :use-button="false" @close="onClosePopup">
    <div class="feedback-popup">
      <template v-if="!isFormSent">
        <img :src="emojiSorry" alt="emojiSorry">
        <div class="feedback-popup__title">
          {{ $t('qic.rate.sorry') }}
        </div>
        <textarea v-model="comment" data-test="feedback-comment" :placeholder="$t('qic.rate.placeholder')" />
        <FlowButton
          class="feedback-popup__button"
          :loading="isSendFeedbackPending"
          @submit="onSendFeedback"
        >
          {{ $t('qic.send') }}
        </FlowButton>
      </template>
      <template v-else>
        <div class="feedback-popup__success">
          <div class="feedback-popup__success-inner">
            <div>{{ $t('qic.thankyou') }}</div>
            <div>{{ $t('qic.rate.received') }}</div>
          </div>
        </div>
        <ButtonQC icon="none" class="feedback-popup__button" @click="onClosePopup">
          {{ $t('qic.ok') }}
        </ButtonQC>
      </template>
    </div>
  </BaseModal>
</template>

<style lang="scss" scoped>
.feedback {
  max-width: 375px;
  padding: 60px 24px 24px;
  border-radius: 32px;
  background: var(--background-transparent, rgb(0 0 0 / 0.75));
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;

  button {
    background-color: transparent;
    border: none;
    cursor: pointer;
  }
}

.feedback__title {
  @include heading-6;

  color: var(--colorContentContrastWhite);
  text-align: center;
  font-weight: 500;
}

.rtl .feedback__title {
  padding: 0 40px;
}

.feedback__container {
  display: flex;
  justify-content: center;
  margin-top: 24px;

  .feedback__item:not(:last-child) {
    margin-inline-end: 48px;
  }
}

.feedback__close {
  position: absolute;
  top: 21px;
  right: 21px;

  img {
    width: 14px;
    height: 14px;
  }
}

.rtl .feedback__close {
  position: absolute;
  top: 21px;
  left: 21px;
  right: auto;
}

.feedback-popup {
  width: 384px;
  display: grid;
  justify-items: center;
  justify-self: center;
  gap: 24px;

  @media (width <= 550px) {
    width: 100%;
  }

  textarea {
    resize: none;
    width: 100%;
    height: 128px;
    border-radius: 12px;
    padding: 16px 12px;
    background-color: #f1f4f6;
  }
}

.feedback-popup__title {
  @include heading-2;

  font-weight: 500;
  text-align: center;
}

.feedback-popup__button {
  width: 100%;
}

.feedback-popup__success {
  background-color: var(--colorBgSrfSuccessGreen);
  color: var(--colorTxtSrfSuccessGreen);
  width: 100%;
  border-radius: 16px;
  text-align: center;
  padding-block: 24px;
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
}

.feedback-popup__success-inner {
  display: grid;
  gap: 16px;
  width: 300px;
  margin: 0 auto;
}
</style>
